import { XCircleIcon } from "@heroicons/react/solid";
import { Upload } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import Cross from "ui/components/icons/Cross";
import { useFileUploader } from "ui/containers/file-uploader";
import { getUploadingMessage } from "ui/lib/helpers";
import { cn } from "ui/lib/utils";
import { VideoRecorder } from "../recorder";
import { RequiredLengthCallout } from "../required-length";

export const VideoUploader = ({
  accept = ["mov", "mp4"],
  onClose,
  onDrop,
  uploadId,
  disabled,
  error,
  requiredLength = null,
}) => {
  const [recordOpen, setRecordOpen] = useState(false);
  const [rejectMessage, setRejectMessage] = useState("");
  const { currentUploads } = useFileUploader();
  const upload = currentUploads[uploadId];

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (...args) => {
      setRejectMessage("");
      onDrop(...args);
    },
    onDropRejected: () => {
      setRejectMessage(`Only ${accept.join(", ")} files are accepted`);
    },
    disabled,
    accept: accept.map(ext => `.${ext}`).join(','),
  });

  if (recordOpen) {
    return (
      <VideoRecorder
        // @ts-ignore
        open={recordOpen}
        onOpenChange={setRecordOpen}
        handleSave={(file) => {
          onDrop([file]);
          setRecordOpen(false);
        }}
      />
    );
  }

  return (
    <motion.div
      key="video-upload"
      className="relative flex flex-col w-full h-full"
    >
      <div
        className={cn("px-8 pt-12 pb-8 flex-col flex-grow", {
          "text-left": !!upload,
          "flex-center text-center": !upload,
        })}
      >
        {/* Header */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-foreground">
            Upload video{disabled ? " (disabled)" : ""}
          </h4>
          {requiredLength && !upload ? (
            <div className="mt-3 mb-2">
              <RequiredLengthCallout kind={requiredLength} />
            </div>
          ) : null}
          <div className="text-sm text-white/40 mt-1">
            {!upload ? (
              `Accepted formats: ${accept.join(", ")}`
            ) : (
              <div className="flex flex-col">
                <span>
                  Feel free to close this pop-up and continue using Apollo while
                  the video is uploading.
                </span>
                <span className="flex items-center mt-4 space-x-2 font-medium text-amber-400/80">
                  <img
                    src="/assets/images/warning.png"
                    className="w-4 h-4"
                    alt="warning"
                  />
                  <span>Do not close Apollo while uploading</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Drop zone or upload progress */}
        {!upload ? (
          <div
            {...getRootProps()}
            className={cn(
              "border border-dashed rounded-xl w-full cursor-pointer transition-colors",
              isDragActive
                ? "border-white/40 bg-white/[0.06]"
                : "border-white/[0.12] bg-white/[0.03] hover:border-white/[0.2] hover:bg-white/[0.05]"
            )}
          >
            <input className="hidden" {...getInputProps()} />
            <div className="flex-col py-10 px-8 space-y-3 flex-center">
              <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center">
                <Upload className="w-5 h-5 text-white/30" />
              </div>
              <div>
                <p className="text-sm text-white/50">
                  Drag & drop your video here
                </p>
                <p className="text-xs text-white/25 mt-1">
                  or click to browse
                </p>
                {rejectMessage && (
                  <p className="text-xs text-red-400/80 mt-2">{rejectMessage}</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="border border-white/[0.08] rounded-xl w-full bg-white/[0.03]">
              <div className="flex items-center px-4 py-3 space-x-3">
                <div className="w-9 h-9 rounded-lg bg-white/[0.06] flex items-center justify-center shrink-0">
                  <Upload className="w-4 h-4 text-white/30" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white/80">
                    Uploading...
                  </p>
                  <p className="text-xs text-white/40">
                    {upload.percentage}% —{" "}
                    {getUploadingMessage(upload.percentage)}
                  </p>
                </div>
              </div>
              <div className="relative mx-4 mb-4">
                <div className="w-full h-1 rounded-full bg-white/[0.08]" />
                <motion.div
                  style={{ width: `${upload.percentage}%` }}
                  className="absolute inset-y-0 left-0 h-1 rounded-full bg-white/60"
                />
              </div>
            </div>
            {error ? (
              <div className="p-3 mt-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className="flex items-center gap-2">
                  <XCircleIcon
                    className="w-4 h-4 text-red-400 shrink-0"
                    aria-hidden="true"
                  />
                  <p className="text-sm text-red-400/80">
                    Something went wrong with the upload
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* OR divider + record button */}
        {!upload ? (
          <div className="mt-4 mx-auto w-full">
            <div className="flex items-center gap-3 w-full">
              <div className="flex-grow h-px bg-white/[0.08]" />
              <span className="text-xs text-white/25 uppercase tracking-wider">or</span>
              <div className="flex-grow h-px bg-white/[0.08]" />
            </div>
            <button
              onClick={() => setRecordOpen(true)}
              className="mt-3 w-full px-4 py-2.5 rounded-xl text-sm text-white/60 hover:text-white/80 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/[0.12] transition-colors"
            >
              Record Video
            </button>
          </div>
        ) : null}
      </div>

      {/* Close button */}
      <div className="absolute top-0 right-0 p-2">
        <button
          aria-label="Close"
          onClick={onClose}
          className="p-3 text-white/30 rounded-full hover:text-white/60 transition-colors"
        >
          <Cross className="w-4" />
        </button>
      </div>
    </motion.div>
  );
};
